import type { ShipDto } from '@/api/hooks'
import { WoodIcon } from '@/components/game/GameIcons'

/**
 * Compact "Réparer [WoodIcon]N" button — the shared macaron for every hull
 * surface (card banner, list-row chip, unseaworthy variants). Kept tight
 * (text-xs, minimal padding) but with a `min-h-8` (32px) tap target so it stays
 * finger-friendly on mobile despite the small visual footprint. `tone` picks the
 * red (unseaworthy) vs neutral (damaged) skin.
 */
function RepairButton({
  wood,
  onRepair,
  repairing,
  tone,
}: {
  wood: number
  onRepair: () => void
  repairing: boolean
  tone: 'neutral' | 'alert'
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        // Guard against bubbling into any row/card-level affordance (e.g. the
        // list row's EditableName) and fire the repair.
        e.preventDefault()
        e.stopPropagation()
        onRepair()
      }}
      disabled={repairing}
      className={`shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 min-h-8 rounded-md border disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
        tone === 'alert'
          ? 'border-red-400/60 bg-red-500/15 text-red-100 hover:bg-red-500/25'
          : 'border-sand-500/35 bg-ocean-900/40 text-sand-100 hover:bg-ocean-500/25 hover:border-sand-500'
      }`}
    >
      Réparer <WoodIcon className="w-3 h-3" />{wood}
    </button>
  )
}

/**
 * Direction C — "Alerte de carène" : nothing when all is well, alert when it
 * matters. Renders the per-ship hull chrome for a DOCKED ship:
 *   - intact (hullIntegrity 100)            → faint muted caption, no button
 *   - damaged but seaworthy (integrity<100) → amber « X % » ribbon + Réparer button
 *   - unseaworthy (seaworthy false)         → red alert banner + Réparer button
 *
 * The left severity stripe on the card itself is NOT rendered here — apply
 * {@link hullStripeClass} to the card container so the border sits on the
 * outer element regardless of this component's own layout.
 */
export function HullAlert({
  ship,
  onRepair,
  repairing,
}: {
  ship: ShipDto
  onRepair: () => void
  repairing: boolean
}) {
  if (ship.hullIntegrity >= 100) {
    return <p className="text-[11.5px] text-ocean-300 italic">Coque saine</p>
  }

  if (!ship.seaworthy) {
    return (
      <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-red-500/40 bg-red-500/[0.13] px-3 py-2.5">
        <span className="text-[13px] font-semibold text-red-300 flex items-center gap-2">
          ⚑ Hors d'état de naviguer : <WoodIcon className="w-3.5 h-3.5" />{ship.repairCostWood}
        </span>
        <RepairButton wood={ship.repairCostWood} onRepair={onRepair} repairing={repairing} tone="alert" />
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2 py-0.5 rounded-full bg-amber-400 text-amber-950">
        {ship.hullIntegrity} %
      </span>
      <RepairButton wood={ship.repairCostWood} onRepair={onRepair} repairing={repairing} tone="neutral" />
    </div>
  )
}

/**
 * Severity stripe class for the card's left edge — apply to the outer card
 * container alongside its existing padding. Empty string for an intact ship
 * (Direction C shows no chrome at all when everything is fine).
 */
export function hullStripeClass(ship: ShipDto): string {
  if (ship.hullIntegrity >= 100) return ''
  if (!ship.seaworthy) return 'border-l-4 border-l-red-500'
  return 'border-l-4 border-l-amber-400'
}

/**
 * Compact hull readout for a mission-launcher row's meta line — the "◆ X %"
 * amber marker (the ◆ + amber colour signal hull state; "coque" is implicit).
 * Renders nothing for an intact ship (Direction C: no chrome when all is well)
 * and nothing for an unseaworthy one either, since that case gets the dedicated
 * blocked-row message instead (see {@link LAUNCHER_UNSEAWORTHY_MESSAGE}).
 */
export function LauncherHullMeta({ ship }: { ship: ShipDto }) {
  if (ship.hullIntegrity >= 100 || !ship.seaworthy) return null
  return (
    <span className="text-amber-300 inline-flex items-center gap-1">
      <span aria-hidden="true">◆</span> {ship.hullIntegrity} %
    </span>
  )
}

/** The blocked-row message for an unseaworthy ship in a mission launcher's picker. */
export const LAUNCHER_UNSEAWORTHY_MESSAGE = "⚑ Hors d'état, répare au chantier"

/**
 * Compact hull chip for the Fleet LIST view (ShipRow) — the same Direction-C
 * design language as {@link HullAlert}, condensed to fit a narrow one-line row:
 *   - intact                → nothing (a per-row caption would clutter a healthy fleet)
 *   - damaged but seaworthy  → amber "X %" chip + the shared compact Réparer button
 *   - unseaworthy            → red "⚑ Hors d'état" pill + the shared compact (red) Réparer button
 *
 * Wording, colours and the WoodIcon cost mirror the card banner; the left stripe
 * is applied on the row itself via {@link hullStripeClass}.
 */
export function HullRowChip({
  ship,
  onRepair,
  repairing,
}: {
  ship: ShipDto
  onRepair: () => void
  repairing: boolean
}) {
  if (ship.hullIntegrity >= 100) return null

  const unseaworthy = !ship.seaworthy
  return (
    <span className="shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap">
      {unseaworthy ? (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-300">
          <span aria-hidden="true">⚑</span> Hors d'état
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950">
          {ship.hullIntegrity} %
        </span>
      )}
      <RepairButton
        wood={ship.repairCostWood}
        onRepair={onRepair}
        repairing={repairing}
        tone={unseaworthy ? 'alert' : 'neutral'}
      />
    </span>
  )
}
